import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { useUser } from '../context/UserContext';
import { useSettings } from '../context/SettingsContext';
import { useToast } from '../hooks/use-toast';
import { useRecaptcha } from '../hooks/useRecaptcha';
import usePageSEO from '../hooks/usePageSEO';
import { getLocalizedSiteName } from '../lib/siteName';

const Login = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, resendVerification } = useUser();
  const { settings } = useSettings();
  const { toast } = useToast();
  const { execute: executeRecaptcha, enabled: recaptchaEnabled } = useRecaptcha();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });
  const verificationEmail = location.state?.verificationEmail || '';
  const lang = i18n.language?.substring(0, 2) || 'en';
  const siteName = getLocalizedSiteName(settings, lang);

  usePageSEO({ title: t('auth.signIn'), noIndex: true });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const recaptchaToken = await executeRecaptcha('login');
      await login(form.email, form.password, recaptchaToken);
      toast({ title: t('auth.signIn'), description: t('auth.welcomeBackMessage', { name: siteName }) });
      navigate('/account');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Login failed';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const email = verificationEmail || form.email;
    if (!email) {
      toast({ title: t('auth.error'), description: t('auth.enterEmailFirst'), variant: 'destructive' });
      return;
    }
    setResending(true);
    try {
      const recaptchaToken = await executeRecaptcha('resend_verification');
      await resendVerification(email, recaptchaToken);
      toast({ title: t('auth.verificationEmailSent'), description: t('auth.checkInbox', { name: siteName }) });
    } catch (error) {
      const msg = error.response?.data?.detail || t('auth.unableToResendVerification');
      toast({ title: t('auth.error'), description: msg, variant: 'destructive' });
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 flex items-center justify-center bg-gray-50" data-testid="login-page">
      <Card className="w-full max-w-md mx-4 shadow-xl">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">{t('auth.signIn')}</CardTitle>
          <p className="text-gray-500 text-sm mt-1">{t('auth.welcomeBack')} {siteName}</p>
        </CardHeader>
        <CardContent>
          {verificationEmail && (
            <div className="mb-4 rounded-md border border-red-100 bg-red-50 p-3 text-sm text-red-900">
              <p>{t('auth.verifyBeforeSignIn', { email: verificationEmail })}</p>
              <button type="button" onClick={handleResendVerification} disabled={resending} className="mt-2 font-semibold text-red-700 hover:underline">
                {resending ? t('contact.form.sending') : t('auth.resendVerificationEmail')}
              </button>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input data-testid="login-email" type="email" className="pl-10" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="john@example.com" />
              </div>
            </div>
            <div>
              <Label>{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input data-testid="login-password" type={showPassword ? 'text' : 'password'} className="pl-10 pr-10" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-sm font-semibold text-red-600 hover:underline">{t('auth.forgotPassword')}</Link>
              </div>
            </div>
            <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={loading} data-testid="login-submit">
              {loading ? t('auth.signingIn') : t('auth.signInBtn')}
            </Button>
            {recaptchaEnabled && (
              <p className="text-xs text-gray-400 text-center mt-2" data-testid="recaptcha-notice">
                Protected by Google <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">reCAPTCHA</a>
              </p>
            )}
          </form>
          <p className="text-center text-sm text-gray-500 mt-6">
            {t('auth.noAccount')} <Link to="/register" className="text-red-600 font-semibold hover:underline">{t('auth.createAccount')}</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
